import { Inject } from '@odinjs/odin';

import { ActionAPI, DeckAPI, FieldAPI, LayoutAPI, ModalAPI, PanelAPI, SortableAPI, TextAPI, ToastService } from '@philips-internal/oswald-ui-components';
import { Value } from '@philips-internal/oswald-ui-core';
import Controller, { OnAfterFetch, OnClick, OnClose, OnMount } from '@philips-internal/oswald-ui-core/lib/client';
import { ClickEvent, FetchEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { HttpService, MessageService } from '@philips-internal/oswald-ui-core/lib/services';
import { applicationContext, foundationContext } from '@philips-internal/oswald-ui-foundation';
import { ISO_DATE_FORMAT } from '@philips-internal/oswald-ui-model/lib/formatters/date/date-utils';

import moment from 'moment';

import { Placement, ToastLevel, ToastType } from '@philips-internal/oswald-ui-constants';
import { FragmentNames, SuepAliases } from '../../codegen';
import { ISO_FULL_DATE_FORMAT } from '../../constants/date-utils';
import { Endpoint } from '../../constants/endpoints';
import { ApplicationRoute } from '../../constants/routes';
import timeService from '../../services/time-service';
import { YesNoType } from '../../utils/enum-utils';
import { FoundationUtils } from '../../utils/foundation-utils';
import { FunctionName, PEP_281Parameters, PEP_9127Parameters } from '../../utils/parameters';
import BaseController from '../BaseController';
import { BrowserService } from '../commons/browser/browser-service';
import CpoeService, { allowViewGroup, CpoeConfigurations, CpoeExpressions, CpoeGroup, OrderGroup } from '../cpoe/services/cpoe-service';
import { SuepModelService } from './suep-model-service';
import { ClinicalNoteSuep, ParamAccess, Screen, SuepCode, SuepCpoe, SuepProfile, SuepProfileTypes, suepProfileTypesOswald } from './suep-types';

@Controller({ fragment: FragmentNames.SUEP })
class SuepController extends BaseController {

  @Inject(SuepAliases.SUEP_CARD_LIST_DECK)
  private suepDeckApi!: DeckAPI;

  @Inject(SuepAliases.SUEP_CARD_LIST_DECK_NON_LAB_EXAMS)
  private suepNonLabDeckApi!: DeckAPI;

  @Inject(SuepAliases.SUEP_CARD_LIST_DECK_DIAGNOSIS)
  private suepDiagnosisDeckApi!: DeckAPI;

  @Inject(SuepAliases.SUEP_CARD_LIST_DECK_LAB_EXAMS)
  private suepLabExamsDeckApi!: DeckAPI;

  @Inject(SuepAliases.SUEP_CARD_LIST_DECK_VITAL_SIGN)
  private suepVitalSignDeckApi!: DeckAPI;

  @Inject(SuepAliases.SUEP_CARD_LIST_DECK_INTAKES_OUTPUTS)
  private suepIntakesOutputsDeckApi!: DeckAPI;

  @Inject(SuepAliases.SUEP_CARD_LIST_DECK_HYDRIC_BALANCE)
  private suepHydricBalanceDeckApi!: DeckAPI;

  @Inject(SuepAliases.PANEL_CLINICAL_NOTES)
  private panelClinicalNotesApi!: PanelAPI;

  @Inject(SuepAliases.PANEL_NON_LAB_EXAMS)
  private panelNonLabApi!: PanelAPI;

  @Inject(SuepAliases.PANEL_DIAGNOSIS)
  private panelDiagnosisApi!: PanelAPI;

  @Inject(SuepAliases.PANEL_LAB_EXAMS)
  private panelLabExamsApi!: PanelAPI;

  @Inject(SuepAliases.PANEL_VITAL_SIGN)
  private panelVitalSignApi!: PanelAPI;

  @Inject(SuepAliases.PANEL_INTAKES_OUTPUTS)
  private panelIntakesOutputsApi!: PanelAPI;

  @Inject(SuepAliases.PANEL_HYDRIC_BALANCE)
  private panelHydricBalanceApi!: PanelAPI;

  @Inject(SuepAliases.SUEP_PANEL_MODAL)
  private suepPanelModalApi!: PanelAPI;

  @Inject(SuepAliases.MODAL_SUEP)
  private suepModalApi!: ModalAPI;

  @Inject(SuepAliases.SUEP_FIELD_DESCRIPTION)
  private suepTextDescriptionApi!: FieldAPI;

  @Inject(SuepAliases.ACTION_VIEW_ALL)
  private clinicalNotesActionApi!: ActionAPI;

  @Inject(SuepAliases.ACTION_VIEW_ALL_NON_LAB_EXAMS)
  private nonLabActionApi!: ActionAPI;

  @Inject(SuepAliases.ACTION_VIEW_ALL_DIAGNOSIS)
  private diagnosisActionApi!: ActionAPI;

  @Inject(SuepAliases.ACTION_VIEW_ALL_LAB_EXAMS)
  private examLabActionApi!: ActionAPI;

  @Inject(SuepAliases.ACTION_VIEW_ALL_VITAL_SIGN)
  private vitalSignActionApi!: ActionAPI;

  @Inject(SuepAliases.ACTION_VIEW_ALL_INTAKES_OUTPUTS)
  private intakesOutputsActionApi!: ActionAPI;

  @Inject(SuepAliases.ACTION_VIEW_ALL_HYDRIC_BALANCE)
  private hydricBalanceActionApi!: ActionAPI;

  @Inject(SuepAliases.SUEP_CARD_LIST_DECK_DEVICES)
  private suepDevicesDeckApi!: DeckAPI;

  @Inject(SuepAliases.PANEL_DEVICES)
  private panelDevicesApi!: PanelAPI;

  @Inject(SuepAliases.ACTION_VIEW_ALL_DEVICES)
  private devicesActionApi!: ActionAPI;

  @Inject(SuepAliases.LAYOUT_ORDER)
  private layoutOrderApi!: LayoutAPI;

  @Inject(SuepAliases.ACTION_OPTION_MODEL)
  private actionOptionModel!: ActionAPI;

  @Inject(SuepAliases.ACTION_VIEW_ALL_CPOE)
  private actionCpoeApi!: ActionAPI;

  @Inject(SuepAliases.PANEL_CPOE)
  private panelCpoeApi!: PanelAPI;

  @Inject(SuepAliases.SORTABLE_CPOE)
  private sortableCpoeApi!: SortableAPI;

  @Inject(SuepAliases.TEXT_NUTRITION)
  private textNutritionApi!: TextAPI;

  @Inject(SuepAliases.TEXT_INFUSIONS_MEDICINES)
  private textInfucionsMedicinesApi!: TextAPI;

  @Inject(SuepAliases.TEXT_EXAMS_PROCEDURES)
  private textExamsProceduresApi!: TextAPI;

  @Inject(SuepAliases.TEXT_GASTHERAPY)
  private textGastherapyApi!: TextAPI;

  @Inject(SuepAliases.TEXT_RECOMMENDATIONS)
  private textRecommendationsApi!: TextAPI;

  @Inject(SuepAliases.TEXT_HEMOTHERAPY)
  private textHemotherapyApi!: TextAPI;

  @Inject(SuepAliases.TEXT_DIALYSIS)
  private textDialysisApi!: TextAPI;

  @Inject(SuepAliases.TEXT_NURSING_INTERVENTIONS)
  private textNursingInterventiosApi!: TextAPI;

  @Inject(SuepAliases.ACTION_VIEW_ALL_SCORE_INDEXES)
  private actionScoreIndexesApi!: ActionAPI;

  @Inject(SuepAliases.PANEL_SCORES_INDEXES)
  private panelScoreIndexesApi!: PanelAPI;

  getParamAccess(module: string, parameter: string): string {
    return FoundationUtils.findModuleParam(module, parameter) as string;
  }

  private showToast(message: string, level: ToastLevel): void {
    ToastService.open({
      dismissible: true,
      highlight: false,
      id: 'suep-card-list',
      level,
      message,
      placement: Placement.BOTTOM,
      showIcon: true,
      timeout: 5000,
      type: ToastType.BANNER,
    })
  }

  @OnMount(SuepAliases.LAYOUT_ORDER)
  handleMountLayoutOrder(): void {
    const currentModel = SuepModelService.currentModel;

    if (currentModel) {
      HttpService.get(Endpoint.SUEP_PROFILE, { suepId: currentModel })
        .then(response => {
          const suepProfileList = response.body as SuepProfile[];
          const templates = suepProfileList.map(item => {
            return suepProfileTypesOswald.find((template) => template.type === item.itemType)?.name
          }).join('""')
          const finalTemplateString = `"${templates}"`;
          this.layoutOrderApi.setTemplateAreas(finalTemplateString);
        }).catch(err => {
          this.showToast(err?.body.errorMessage, ToastLevel.ERROR);
        })
    }

    timeService.getCurrentDateTime()
      .then(currentDateTime => {
        const referenceDateTime = moment(currentDateTime).format(ISO_FULL_DATE_FORMAT);
        this.context.set({ currentDateTime: referenceDateTime })
      })
      .catch(() => this.context.set({ currentDateTime: moment().format(ISO_FULL_DATE_FORMAT) }));
  }

  private checkAvailability(): void {
    const loadingClinicalNotes = this.context.get('loadingClinicalNotes');
    const loadingNonLab = this.context.get('loadingNonLab');
    const loadingDiagnosis = this.context.get('loadingDiagnosis');
    const loadingLabExams = this.context.get('loadingLabExams');
    const loadingVitalSigns = this.context.get('loadingVitalSigns');
    const loadingIntakesOutputs = this.context.get('loadingIntakesOutputs');
    const loadingHydric = this.context.get('loadingHydric');
    const loadingDevices = this.context.get('loadingDevices');
    const verification = loadingClinicalNotes || loadingNonLab ||
      loadingDiagnosis || loadingLabExams || loadingVitalSigns ||
      loadingIntakesOutputs || loadingHydric || loadingDevices;
    if (!verification) {
      this.actionOptionModel.setEnabled(true);
    }
  }

  private showModule(modulePermission: string, moduleTitle: string): void {
    if (modulePermission === YesNoType.YES) {
      applicationContext.showModuleOption(FunctionName.PEP_9127, moduleTitle);
    }
  }

  private showModuleOptions(): void {
    const clinicalNotes = this.getParamAccess(FunctionName.PEP_9127, PEP_9127Parameters.ALLOW_ACCESS_CLINICAL_NOTES);
    const vitalSign = this.getParamAccess(FunctionName.PEP_9127, PEP_9127Parameters.ALLOW_ACESS_VITAL_SIGN);
    const labExams = this.getParamAccess(FunctionName.PEP_9127, PEP_9127Parameters.ALLOW_ACESS_LAB_EXAMS);
    const nonLabExams = this.getParamAccess(FunctionName.PEP_9127, PEP_9127Parameters.ALLOW_ACESS_NON_LAB_EXAMS);
    const registerVitalSign = this.getParamAccess(FunctionName.PEP_9127, PEP_9127Parameters.OPEN_NEW_REGISTER_VITAL_SIGN);
    const securityAlert = this.getParamAccess(FunctionName.PEP_9127, PEP_9127Parameters.ALLOW_ACCESS_TO_VIEW_SECURITY_ALERTS);
    const diagnosis = this.getParamAccess(FunctionName.PEP_9127, PEP_9127Parameters.ALLOW_ACCESS_TO_VIEW_DIAGNOSIS);
    const prescriptions = this.getParamAccess(FunctionName.PEP_9127, PEP_9127Parameters.ALLOW_YOU_TO_VIEWS_CURRENT_PRESCRIPTIONS);
    const respiratoryMonitoring = this.getParamAccess(FunctionName.PEP_9127, PEP_9127Parameters.ALLOW_ACCESS_RESPIRATORY_MONITORING);
    const devices = this.getParamAccess(FunctionName.PEP_9127, PEP_9127Parameters.ALLOW_ACCESS_DEVICES);
    const intakesAndOutputs = this.getParamAccess(FunctionName.PEP_9127, PEP_9127Parameters.ALLOW_ACCESS_INTAKES_AND_OUTPUTS);
    const hydricBalance = this.getParamAccess(FunctionName.PEP_9127, PEP_9127Parameters.ALLOW_ACCESS_HYDRIC_BALANCE);
    const scoreAndIndexes = this.getParamAccess(FunctionName.PEP_9127, PEP_9127Parameters.ALLOW_ACCESS_SCORES_AND_INDEXES);
    const healthHistory = this.getParamAccess(FunctionName.PEP_9127, PEP_9127Parameters.ALLOW_ACCESS_HEALTH_HISTORY);
    const woundsDressings = this.getParamAccess(FunctionName.PEP_9127, PEP_9127Parameters.ALLOW_ACCESS_WOUNDS_AND_DRESSINGS);

    const woundsDressingsModule = woundsDressings === ParamAccess.ONE ? YesNoType.YES : YesNoType.NO;
    const vitalSignModule = vitalSign === YesNoType.YES && respiratoryMonitoring === YesNoType.YES ? YesNoType.YES : YesNoType.NO;
    const intakesOuputsModule = intakesAndOutputs === YesNoType.NO && hydricBalance === YesNoType.NO ? YesNoType.NO : YesNoType.YES;

    this.showModule(clinicalNotes, 'function.clinical-notes');
    this.showModule(vitalSignModule, 'function.vital-signs');
    this.showModule(registerVitalSign, 'function.vital-signs');
    this.showModule(labExams, 'function.laboratory-exams');
    this.showModule(nonLabExams, 'function.non-lab-exams');
    this.showModule(diagnosis, 'healh.diagnosis');
    this.showModule(securityAlert, 'text.security-alert');
    this.showModule(prescriptions, 'text.prescriptions');
    this.showModule(intakesOuputsModule, 'text.intakes-and-outputs');
    this.showModule(devices, 'title.form.devices-list');
    this.showModule(scoreAndIndexes, 'title.form.score-and-indexes');
    this.showModule(healthHistory, 'text.health-history');
    this.showModule(woundsDressingsModule, 'text.wounds-dressings');
  }

  handleSuepCode(): void {
    const { encounterId, patientId } = this.historyService.getCurrentRouteParams();
    HttpService.get(Endpoint.SUEP_CODE, { encounterId })
      .then(response => {
        const suepId = response.body as SuepCode;
        this.context.set({ encounterId, patientId, suepId: String(suepId.suepSequenceNumber) });
        this.handleSuepProfile(String(suepId.suepSequenceNumber));
      });
  }

  @OnClick(SuepAliases.ACTION_OPTION_MODEL)
  handleClickActionModel(): void {
    const suepId = this.context.get('suepId');
    applicationContext.redirect(ApplicationRoute.SUEP_MODEL_OPTIONS, { suepId: suepId });
  }

  async handleGenerateSuep(suepProfiles: SuepProfile[], suepId: string): Promise<void> {
    const { encounterId, patientId } = this.historyService.getCurrentRouteParams();
    const body = {
      encounterId,
      patientId,
      suepId
    } as Record<string, Value>;

    const hasVitalSignsOrScoreIndexes = suepProfiles.some(
      ({ itemType }) =>
        itemType === SuepProfileTypes.VITAL_SIGNS ||
        itemType === SuepProfileTypes.SCORE_INDEXES
    );

    if (hasVitalSignsOrScoreIndexes) {
      await HttpService.post(Endpoint.SUEP_GENERATE, undefined, { body });
    }
  }

  handleSuepProfile(idSuep: string): void {
    const currentModel = SuepModelService.currentModel;
    const suepId = currentModel || idSuep;

    HttpService.get(Endpoint.SUEP_PROFILE, { suepId })
      .then(response => {
        const suepProfiles = response.body as SuepProfile[];

        const panelByType: Partial<Record<SuepProfileTypes, PanelAPI>> = {
          [SuepProfileTypes.CLINICAL_NOTES]:   this.panelClinicalNotesApi,
          [SuepProfileTypes.NON_LAB_EXAMS]:    this.panelNonLabApi,
          [SuepProfileTypes.DIAGNOSIS]:        this.panelDiagnosisApi,
          [SuepProfileTypes.LAB_EXAMS]:        this.panelLabExamsApi,
          [SuepProfileTypes.VITAL_SIGNS]:      this.panelVitalSignApi,
          [SuepProfileTypes.INTAKES_OUTPUTS]:  this.panelIntakesOutputsApi,
          [SuepProfileTypes.HYDRIC_BALANCE]:   this.panelHydricBalanceApi,
          [SuepProfileTypes.DEVICES]:          this.panelDevicesApi,
          [SuepProfileTypes.CPOE]:             this.panelCpoeApi,
          [SuepProfileTypes.SCORE_INDEXES]:    this.panelScoreIndexesApi,
        };

        this.context.set({
          loadingClinicalNotes: false,
          loadingCpoe: false,
          loadingDevices: false,
          loadingDiagnosis: false,
          loadingHydric: false,
          loadingIntakesOutputs: false,
          loadingLabExams: false,
          loadingNonLab: false,
          loadingVitalSigns: false,
          loadingWoundsDressings: false,
          suepId: suepId
        });

        this.handleGenerateSuep(suepProfiles, suepId);

        suepProfiles.forEach(({ itemType }) => {
          if (itemType === SuepProfileTypes.CPOE) {
            this.getConfigurationsCpoe();
          }
          panelByType[itemType as SuepProfileTypes]?.setVisible(true);
        });
      })
  }

  @OnMount(SuepAliases.SUEP_CARD_LIST_DECK)
  showClinicalNotesProfile(): void {
    const suepId = this.context.get('suepId');
    const { encounterId, patientId } = this.historyService.getCurrentRouteParams();
    const params = {
      encounterId,
      patientId,
      suepId
    }

    this.context.set({ loadingClinicalNotes: true })
    HttpService.get(Endpoint.SUEP_CLINICAL_NOTES_TYPES, params)
      .then(response => {
        const responseBody = response.body as ClinicalNoteSuep;
        this.handleClinicalNotesList(responseBody);
      }).finally(() => {
        this.context.set({ loadingClinicalNotes: false });
        this.checkAvailability();
      })
  }

  handleClinicalNotesList(clinicalNoteSuep: ClinicalNoteSuep): void {
    const { encounterId, patientId } = this.historyService.getCurrentRouteParams();
    const clinicalNotesTypesId = clinicalNoteSuep.clinicalNoteType;
    const params = {
      clinicalNotesTypesId,
      encounterId,
      endDate: moment().format(ISO_DATE_FORMAT),
      limit: 4,
      page: 0,
      patientId,
      startDate: moment().subtract(1, 'months').format(ISO_DATE_FORMAT),
    }

    this.context.set({ loadingClinicalNotes: true });
    this.suepDeckApi.setLoading(true);

    HttpService.get(Endpoint.CLINICAL_NOTES_LIST, params)
      .then(response => {
        const responseBody = response.body as Record<string, Value>[];
        if (responseBody) {
          const clinicalNotes = responseBody.map(notes => ({
            clinicalNoteId: notes.cdEvolucao,
            description: notes.dsTipoNotaClinica,
            eStatus: notes.ieStatus,
            evolution: notes.dsEvolucao,
            subtitle: moment(String(notes.dtEvolucao)).format('L LT'),
            title: notes.professionalName,
            type: SuepProfileTypes.CLINICAL_NOTES
          }));
          this.suepDeckApi.setData(clinicalNotes);
        }
      }).finally(() => {
        this.suepDeckApi.setLoading(false);
        this.context.set({ loadingClinicalNotes: false });
        this.checkAvailability();
      })
  }

  @OnMount(SuepAliases.SUEP_CARD_LIST_DECK_NON_LAB_EXAMS)
  showNonLabExamsProfile(): void {
    const suepId = this.context.get('suepId');
    const { encounterId, patientId } = this.historyService.getCurrentRouteParams();
    const params = {
      encounterId,
      patientId,
      suepId
    }

    this.context.set({ loadingNonLab: true });
    HttpService.get(Endpoint.NON_LAB_EXAMS_TYPES, params)
      .then(response => {
        const responseBody = response.body as Record<string, Value>[];

        this.handleNonLabExamsList(responseBody);
      }).finally(() => {
        this.context.set({ loadingNonLab: false });
        this.checkAvailability();
      })
  }

  handleNonLabExamsList(nonLabExamsTypes: Record<string, Value>[]): void {
    const { encounterId, patientId } = this.historyService.getCurrentRouteParams();
    const paramViewAnatomyReport = this.getParamAccess(FunctionName.PEP_281, PEP_281Parameters.VIEW_ANATOMY_REPORT);
    const paramRequiresReportRelease = this.getParamAccess(FunctionName.PEP_281, PEP_281Parameters.REQUIRES_REPORT_RELEASE);
    const paramViewTyping = this.getParamAccess(FunctionName.PEP_281, PEP_281Parameters.VIEW_TYPING);
    const paramShowOnlyApproved = this.getParamAccess(FunctionName.PEP_281, PEP_281Parameters.SHOW_ONLY_APPROVED_NON_LAB_EXAMS);
    const examTypes = nonLabExamsTypes.map(type => type.examType);

    this.suepNonLabDeckApi.setLoading(true);
    this.context.set({ loadingNonLab: true });
    const params = {
      cdTipoExame: String(examTypes),
      encounterId,
      limit: 4,
      page: 0,
      paramRequiresReportRelease,
      paramShowOnlyApproved,
      paramViewAnatomyReport,
      paramViewTyping,
      patientId
    }

    HttpService.get(Endpoint.NON_LAB_EXAMS_LIST, params)
      .then(response => {
        const responseBody = response.body as Record<string, Value>[];

        if (responseBody) {
          const nonLabExams = responseBody.map(exams => ({
            description: exams.dsTituloLaudo,
            evolution: exams.nrSequencia,
            isReleased: exams.dtLiberacao,
            subtitle: moment(String(exams.dtExame)).format('L LT'),
            type: SuepProfileTypes.NON_LAB_EXAMS
          }));
          this.suepNonLabDeckApi.setData(nonLabExams);
        }
      }).finally(() => {
        this.suepNonLabDeckApi.setLoading(false);
        this.context.set({ loadingNonLab: false });
        this.checkAvailability();
      })
  }

  @OnMount({ target: SuepAliases.SUEP_CARD_LIST_DECK_DIAGNOSIS })
  showDiagnosisProfile(): void {
    const suepId = this.context.get('suepId');
    const { encounterId, patientId } = this.historyService.getCurrentRouteParams();
    const params = {
      encounterId,
      patientId,
      suepId
    }

    this.suepDiagnosisDeckApi.setLoading(true);
    this.context.set({ loadingDiagnosis: true });

    HttpService.get(Endpoint.SUEP_DIAGNOSIS_LIST, params)
      .then(response => {
        const responseBody = response.body as Record<string, Value>[];

        if (responseBody) {
          const diagnosisList = responseBody.map(diagnosis => ({
            description: diagnosis.diseaseDescription,
            evolution: diagnosis.sequenceNumber,
            subtitle: moment(String(diagnosis.diagnosisDate)).format('L LT'),
            title: diagnosis.descriptionType,
          }));
          this.suepDiagnosisDeckApi.setData(diagnosisList);
        }
      }).finally(() => {
        this.suepDiagnosisDeckApi.setLoading(false);
        this.context.set({ loadingDiagnosis: false });
        this.checkAvailability();
      })
  }

  @OnMount({ target: SuepAliases.SUEP_CARD_LIST_DECK_LAB_EXAMS })
  showLabExamsProfile(): void {
    const suepId = this.context.get('suepId');
    const { encounterId, patientId } = this.historyService.getCurrentRouteParams();
    const params = {
      encounterId,
      limit: 4,
      page: 0,
      patientId,
      suepId
    }

    this.suepLabExamsDeckApi.setLoading(true);
    this.context.set({ loadingLabExams: true });

    HttpService.get(Endpoint.LAB_EXAMS_LIST, params)
      .then(response => {
        const responseBody = response.body as Record<string, Value>[];
        const normalizedData = responseBody.map(data => ({
          ...data,
          dtResultado: moment(String(data.dtResultado)).format('L LT'),
        }))
        if (normalizedData) {
          this.suepLabExamsDeckApi.setData(normalizedData);
        }
      }).finally(() => {
        this.suepLabExamsDeckApi.setLoading(false);
        this.context.set({ loadingLabExams: false });
        this.checkAvailability();
      })
  }

  @OnMount({ target: SuepAliases.SUEP_CARD_LIST_DECK_VITAL_SIGN })
  showVitalSignProfile(): void {
    const { encounterId, patientId } = this.historyService.getCurrentRouteParams();
    const params = {
      encounterId,
      patientId
    }

    this.suepVitalSignDeckApi.setLoading(true);
    this.context.set({ loadingVitalSigns: true });
    HttpService.get(Endpoint.SUEP_VITAL_SIGN_LIST, params)
      .then(response => {
        const responseBody = response.body as Record<string, Value>[];
        if (responseBody) {
          const vitalSignsList = responseBody.map(vitalSign => ({
            description: vitalSign.description,
            informationValue: vitalSign.informationValue,
            outOfRange: vitalSign.outOfRange,
            subtitle: moment(String(vitalSign.createdDate)).format('L LT'),
            type: SuepProfileTypes.VITAL_SIGNS
          }));
          this.suepVitalSignDeckApi.setData(vitalSignsList);
        }
      }).finally(() => {
        this.suepVitalSignDeckApi.setLoading(false);
        this.context.set({ loadingVitalSigns: false });
        this.checkAvailability();
      })
  }

  @OnMount({ target: SuepAliases.SUEP_CARD_LIST_DECK_INTAKES_OUTPUTS })
  showIntakesOutputsProfile(): void {
    const { encounterId, patientId } = this.historyService.getCurrentRouteParams();
    const params = { encounterId, patientId }

    this.suepIntakesOutputsDeckApi.setLoading(true);
    this.context.set({ loadingIntakesOutputs: true });
    HttpService.get(Endpoint.SUEP_INTAKES_OUTPUTS_LIST, params)
      .then(response => {
        const responseBody = response.body as Record<string, Value>[];

        if (responseBody) {
          const intakesOutputsList = responseBody.map(intakeOutput => ({
            description: intakeOutput.descriptionType,
            evolution: intakeOutput.sequenceNumber,
            subtitle: moment(String(intakeOutput.recordDate)).format('L LT'),
            type: intakeOutput.gainLoss
          }));
          this.suepIntakesOutputsDeckApi.setData(intakesOutputsList);
        }
      }).finally(() => {
        this.suepIntakesOutputsDeckApi.setLoading(false);
        this.context.set({ loadingIntakesOutputs: false });
        this.checkAvailability();
      })
  }

  @OnMount({ target: SuepAliases.SUEP_CARD_LIST_DECK_HYDRIC_BALANCE })
  showHydricBalanceProfile(): void {
    const { encounterId, patientId } = this.historyService.getCurrentRouteParams();
    const params = { encounterId, patientId }
    this.suepHydricBalanceDeckApi.setLoading(true);
    this.context.set({ loadingHydric: true });
    HttpService.get(Endpoint.SUEP_HYDRIC_BALANCE_LIST, params)
      .then(response => {
        const responseBody = response.body as Record<string, Value>[];

        if (responseBody.length > 1) {
          const dailyHydricBalanceAnalyses = {
            dailyHydricBalanceAnalyses: responseBody
          }
          this.suepHydricBalanceDeckApi.setData([dailyHydricBalanceAnalyses]);
        }
      }).finally(() => {
        this.suepHydricBalanceDeckApi.setLoading(false);
        this.context.set({ loadingHydric: false });
        this.checkAvailability();
      })
  }

  @OnMount({ target: SuepAliases.SUEP_CARD_LIST_DECK_DEVICES })
  showDevicesProfile(): void {
    const { encounterId } = this.historyService.getCurrentRouteParams();

    this.suepDevicesDeckApi.setLoading(true);
    this.context.set({ loadingDevices: true });
    HttpService.get<Record<string, Value>[]>(Endpoint.SUEP_DEVICES_LIST, { encounterId })
      .then(response => {
        if (!response.body) {
          return;
        }

        const body = response.body;

        if (body.length > 0) {
          this.suepDevicesDeckApi.setData(body);
        }

      })
      .finally(() => {
        this.suepDevicesDeckApi.setLoading(false);
        this.context.set({ loadingDevices: false });
        this.checkAvailability();
      })
  }

  @OnMount({ target: SuepAliases.MODAL_SUEP })
  onModalMount(): void {
    this.suepModalApi.setTitle(this.context.get('title'));
  }

  @OnMount({ target: SuepAliases.SUEP_FIELD_DESCRIPTION })
  onModalTextMount(): void {
    const { context, suepTextDescriptionApi } = this;
    suepTextDescriptionApi.setValue(context.get('evolution'));
  }

  private hideAllModuleOptions(): void {
    applicationContext.hideModuleOption(FunctionName.PEP_9127, 'title.form.score-and-indexes');
    applicationContext.hideModuleOption(FunctionName.PEP_9127, 'title.form.devices-list');
    applicationContext.hideModuleOption(FunctionName.PEP_9127, 'function.clinical-notes');
    applicationContext.hideModuleOption(FunctionName.PEP_9127, 'function.vital-signs');
    applicationContext.hideModuleOption(FunctionName.PEP_9127, 'function.laboratory-exams');
    applicationContext.hideModuleOption(FunctionName.PEP_9127, 'function.non-lab-exams');
    applicationContext.hideModuleOption(FunctionName.PEP_9127, 'function.vital-signs');
    applicationContext.hideModuleOption(FunctionName.PEP_9127, 'text.security-alert');
    applicationContext.hideModuleOption(FunctionName.PEP_9127, 'healh.diagnosis');
    applicationContext.hideModuleOption(FunctionName.PEP_9127, 'text.prescriptions');
    applicationContext.hideModuleOption(FunctionName.PEP_9127, 'text.intakes-and-outputs');
    applicationContext.hideModuleOption(FunctionName.PEP_9127, 'text.health-history');
    applicationContext.hideModuleOption(FunctionName.PEP_9127, 'text.wounds-dressings');
  }

  @OnMount({ target: SuepAliases.LAYOUT_SUEP })
  onLayoutMount(): void {
    const { encounterId, patientId, nrSequencia } = this.historyService.getCurrentRouteParams();
    const currentModel = SuepModelService.currentModel;

    this.context.set({
      browser: BrowserService.detectBrowser(),
      encounterId,
      nrSequencia,
      patientId,
    });

    this.hideAllModuleOptions();
    this.actionOptionModel.setEnabled(false);

    if (!currentModel) {
      this.handleSuepCode();
    } else {
      this.context.set({ encounterId, patientId, suepId: String(currentModel) });
      this.handleSuepProfile(String(currentModel));
    }

    this.showModuleOptions();
  }

  @OnMount(SuepAliases.ACTION_VIEW_ALL_DEVICES)
  onDevicesActionMount(): void {
    this.devicesActionApi.setEnabled(true);
  }

  @OnMount({ target: SuepAliases.ACTION_VIEW_ALL })
  onClinicalNoteActionMount(): void {
    const clinicalNotes = this.getParamAccess(FunctionName.PEP_9127, PEP_9127Parameters.ALLOW_ACCESS_CLINICAL_NOTES);
    const allowClinicalNotes = clinicalNotes === YesNoType.YES;

    if (allowClinicalNotes) {
      this.clinicalNotesActionApi.setEnabled(true);
    }
  }

  @OnMount({ target: SuepAliases.ACTION_VIEW_ALL_NON_LAB_EXAMS })
  onNonLabActionMount(): void {
    const nonLabExams = this.getParamAccess(FunctionName.PEP_9127, PEP_9127Parameters.ALLOW_ACESS_NON_LAB_EXAMS);
    const allowNonLabExams = nonLabExams === YesNoType.YES;

    if (allowNonLabExams) {
      this.nonLabActionApi.setEnabled(true);
    }
  }

  @OnMount({ target: SuepAliases.ACTION_VIEW_ALL_DIAGNOSIS })
  onDiagnosisActionMount(): void {
    const diagnosis = this.getParamAccess(FunctionName.PEP_9127, PEP_9127Parameters.ALLOW_ACCESS_TO_VIEW_DIAGNOSIS);
    const allowDiagnosis = diagnosis === YesNoType.YES;

    if (allowDiagnosis) {
      this.diagnosisActionApi.setEnabled(true);
    }
  }

  @OnMount({ target: SuepAliases.ACTION_VIEW_ALL_LAB_EXAMS })
  onExamLabActionMount(): void {
    const exams = this.getParamAccess(FunctionName.PEP_9127, PEP_9127Parameters.ALLOW_ACESS_LAB_EXAMS);
    const allowExams = exams === YesNoType.YES;

    if (allowExams) {
      this.examLabActionApi.setEnabled(true);
    }
  }

  @OnMount({ target: SuepAliases.ACTION_VIEW_ALL_VITAL_SIGN })
  onVitalSignActionMount(): void {
    const vitalSign = this.getParamAccess(FunctionName.PEP_9127, PEP_9127Parameters.ALLOW_ACESS_VITAL_SIGN);
    const respiratoryMonitoring = this.getParamAccess(FunctionName.PEP_9127, PEP_9127Parameters.ALLOW_ACCESS_RESPIRATORY_MONITORING);
    const allowVitalSign = vitalSign === YesNoType.YES || respiratoryMonitoring === YesNoType.YES;

    if (allowVitalSign) {
      this.vitalSignActionApi.setEnabled(true);
    }
  }

  @OnMount({ target: SuepAliases.ACTION_VIEW_ALL_INTAKES_OUTPUTS })
  onIntakesOutputsActionMount(): void {
    const intakesOutputs = this.getParamAccess(FunctionName.PEP_9127, PEP_9127Parameters.ALLOW_ACCESS_INTAKES_AND_OUTPUTS);
    const allowIntakesOutputs = intakesOutputs === YesNoType.YES;

    if (allowIntakesOutputs) {
      this.intakesOutputsActionApi.setEnabled(true);
    }
  }

  @OnMount({ target: SuepAliases.ACTION_VIEW_ALL_HYDRIC_BALANCE })
  onHydricBalanceActionMount(): void {
    const hydricBalance = this.getParamAccess(FunctionName.PEP_9127, PEP_9127Parameters.ALLOW_ACCESS_HYDRIC_BALANCE);
    const allowHydricBalance = hydricBalance === YesNoType.YES;

    if (allowHydricBalance) {
      this.hydricBalanceActionApi.setEnabled(true);
    }
  }

  @OnMount(SuepAliases.ACTION_VIEW_ALL_CPOE)
  onCpoeActionMount(): void {
    const allowCpoe = foundationContext.getModule(FunctionName.CPOE_9138);

    if (allowCpoe) {
      this.actionCpoeApi.setEnabled(true);
    }
  }

  @OnMount({ target: SuepAliases.ACTION_VIEW_ALL_SCORE_INDEXES })
  handleScoreIndexesActionMount(): void {
    const scoreIndexes = this.getParamAccess(FunctionName.PEP_9127, PEP_9127Parameters.ALLOW_ACCESS_SCORES_AND_INDEXES);
    const allowScoreIndexes = scoreIndexes === YesNoType.YES;

    if (allowScoreIndexes) {
      this.actionScoreIndexesApi.setEnabled(true);
    }
  }

  @OnMount({ target: SuepAliases.SORTABLE_CPOE })
  handleMountSortableCPOE(): void {
    const cpoeConfigurations = this.context.get('cpoeConfigurations') as CpoeConfigurations;
    this.setOrderGroups(cpoeConfigurations);
  }

  getConfigurationsCpoe(): void {
    CpoeService.loadConfigurationsCpoe()
      .then((cpoeConfigurations: CpoeConfigurations) => {
        this.context.set({ cpoeConfigurations });
      });
  }

  setOrderGroups(configurationsCpoe: CpoeConfigurations): void {
    const orderedGroups: OrderGroup[] = [
      {
        group: CpoeGroup.NUTRITION, order: configurationsCpoe.orderOralDiet ?? 0,
        view: allowViewGroup(configurationsCpoe.ruleOralDiet)
      },
      {
        group: CpoeGroup.INFUSIONS_MEDICINES, order: configurationsCpoe.orderMedicine ?? 1,
        view: allowViewGroup(configurationsCpoe.ruleMedicine)
      },
      {
        group: CpoeGroup.EXAMS_PROCEDURES, order: configurationsCpoe.orderExam ?? 2,
        view: allowViewGroup(configurationsCpoe.ruleExam)
      },
      {
        group: CpoeGroup.GASTHERAPY, order: configurationsCpoe.orderGasTherapy ?? 3,
        view: allowViewGroup(configurationsCpoe.ruleGasTherapy)
      },
      {
        group: CpoeGroup.RECOMMENDATIONS, order: configurationsCpoe.orderRecommendation ?? 4,
        view: allowViewGroup(configurationsCpoe.ruleRecommendation)
      },
      {
        group: CpoeGroup.HEMOTHERAPY, order: configurationsCpoe.orderHemotherapy ?? 5,
        view: allowViewGroup(configurationsCpoe.ruleHemotherapy)
      },
      {
        group: CpoeGroup.DIALYSIS, order: configurationsCpoe.orderDialysis ?? 6,
        view: allowViewGroup(configurationsCpoe.ruleDialysis)
      },
      {
        group: CpoeGroup.NURSING_INTERVENTIONS, order: configurationsCpoe.orderInterventation ?? 7,
        view: allowViewGroup(configurationsCpoe.ruleInterventation)
      },
    ]?.sort(this.sortGroups);

    const order = orderedGroups.filter(item => !!item.view).map(item => item.group);
    this.sortableCpoeApi.setOrder(order ?? []);
  }

  sortGroups(orderGroupA: OrderGroup, orderGroupB: OrderGroup): number {
    return orderGroupA.order - orderGroupB.order;
  }

  @OnMount({ target: SuepAliases.TEXT_NUTRITION })
  handleMountTextNutrition(): void {
    const suepCpoe = this.context.get('suepCpoe') as SuepCpoe[];
    this.textNutritionApi.setValue(MessageService.translate(CpoeExpressions.Nutrition, { count: suepCpoe[0].nutrition }));
  }

  @OnMount({ target: SuepAliases.TEXT_INFUSIONS_MEDICINES })
  handleMountTextInfusionsMedicines(): void {
    const suepCpoe = this.context.get('suepCpoe') as SuepCpoe[];
    this.textInfucionsMedicinesApi.setValue(MessageService.translate(CpoeExpressions.Infusion, { count: suepCpoe[0].infusionsMedicines }));
  }

  @OnMount({ target: SuepAliases.TEXT_EXAMS_PROCEDURES })
  handleMountTextExamsProcedures(): void {
    const suepCpoe = this.context.get('suepCpoe') as SuepCpoe[];
    this.textExamsProceduresApi.setValue(MessageService.translate(CpoeExpressions.Exam, { count: suepCpoe[0].examsProcedures }));
  }

  @OnMount({ target: SuepAliases.TEXT_GASTHERAPY })
  handleMountTextGastherapy(): void {
    const suepCpoe = this.context.get('suepCpoe') as SuepCpoe[];
    this.textGastherapyApi.setValue(MessageService.translate(CpoeExpressions.Gastherapy, { count: suepCpoe[0].gastherapy }));
  }

  @OnMount({ target: SuepAliases.TEXT_RECOMMENDATIONS })
  handleMountTextRecommendations(): void {
    const suepCpoe = this.context.get('suepCpoe') as SuepCpoe[];
    this.textRecommendationsApi.setValue(MessageService.translate(CpoeExpressions.Recommendation, { count: suepCpoe[0].recommendations }));
  }

  @OnMount({ target: SuepAliases.TEXT_HEMOTHERAPY })
  handleMountTextHemotherapy(): void {
    const suepCpoe = this.context.get('suepCpoe') as SuepCpoe[];
    this.textHemotherapyApi.setValue(MessageService.translate(CpoeExpressions.Hemotherapy, { count: suepCpoe[0].hemotherapy }));
  }

  @OnMount({ target: SuepAliases.TEXT_DIALYSIS })
  handleMountTextDialysis(): void {
    const suepCpoe = this.context.get('suepCpoe') as SuepCpoe[];
    this.textDialysisApi.setValue(MessageService.translate(CpoeExpressions.Dialysis, { count: suepCpoe[0].dialysis }));
  }

  @OnMount({ target: SuepAliases.TEXT_NURSING_INTERVENTIONS })
  handleMountTextNursingInterventions(): void {
    const suepCpoe = this.context.get('suepCpoe') as SuepCpoe[];
    this.textNursingInterventiosApi.setValue(MessageService.translate(CpoeExpressions.Interventation, { count: suepCpoe[0].nursingInterventions }));
  }

  @OnAfterFetch({ target: SuepAliases.FETCH_CPOE })
  handleFetchCpoe(event: FetchEvent): void {
    const suepCpoe = event.data as SuepCpoe[];
    this.context.set({ suepCpoe });
    this.checkAvailability();
  }

  @OnAfterFetch({ target: SuepAliases.FETCH_SCORE_INDEXES })
  handleFetchScoreIndexes(): void {
    this.checkAvailability();
  }

  @OnClick({ target: SuepAliases.SUEP_CARD })
  onCardClick(event: ClickEvent): void {
    const { clinicalNoteId, eStatus, evolution, title } = event.value;
    this.context.set({ evolution, title });
    applicationContext.redirect(ApplicationRoute.CLINICAL_NOTES_VIEWER, { cdEvolucao: clinicalNoteId, eStatus });
  }

  @OnClick({ target: SuepAliases.SUEP_CARD_DIAGNOSIS })
  onDiagnosisCardClick(event: ClickEvent): void {
    const { evolution } = event.value;
    const { encounterId } = this.historyService.getCurrentRouteParams();
    applicationContext.redirect(ApplicationRoute.DIAGNOSIS_VIEWER, { encounterId, sequenceNumber: evolution });
  }

  @OnClick({ target: SuepAliases.SUEP_CARD_INTAKES_OUTPUTS })
  onIntakesOutputsCardClick(event: ClickEvent): void {
    const { evolution } = event.value;
    const { encounterId, patientId } = this.historyService.getCurrentRouteParams();

    applicationContext.redirect(ApplicationRoute.INTAKES_OUTPUTS_VIEWER, {
      encounterId,
      patientId,
      sequenceNumber: evolution
    });
  }

  @OnClick({ target: SuepAliases.SUEP_CARD_LAB_EXAMS })
  onLabExamCardClick(event: ClickEvent): void {
    const { nrSequencia, nrPrescricao, nrSeqResultado } = event.value;
    const baseUrl = `${ApplicationRoute.ELECTRONIC_PATIENT_CHART}${ApplicationRoute.PATIENT_LIST}${ApplicationRoute.LAB_EXAMS_LIST}`;

    if (nrPrescricao) {
      applicationContext.redirect(`${baseUrl}${ApplicationRoute.LAB_EXAMS_DETAIL_PRESCRIPTION}`, {
        prescriptionId: nrPrescricao,
        sequenceId: nrSequencia
      });
    } else if (nrSeqResultado) {
      applicationContext.redirect(`${baseUrl}${ApplicationRoute.LAB_EXAMS_DETAIL_RESULT}`, {
        resultId: nrSeqResultado,
        sequenceId: nrSequencia
      });
    }

  }

  @OnClick({ target: SuepAliases.SUEP_CARD_NON_LAB_EXAMS })
  onNonLabCardClick(event: ClickEvent): void {
    const { encounterId, patientId } = this.historyService.getCurrentRouteParams();
    const { evolution: nrSequencia } = event.value;

    HttpService.post(Endpoint.REGISTER_NON_LAB_EXAM_ACCESS_LOG, {
      encounterId,
      nrSequencia,
      patientId,
    }).then(() => {
      applicationContext.redirect(ApplicationRoute.ELECTRONIC_PATIENT_CHART +
        ApplicationRoute.PATIENT_LIST +
        ApplicationRoute.NON_LAB_EXAMS_LIST +
        ApplicationRoute.NON_LAB_EXAM_DETAIL, { nrSequencia });
    });
  }

  @OnClick(SuepAliases.DEVICES_CARD)
  onDevicesCardClick(event: ClickEvent): void {
    const { encounterId, patientId } = this.historyService.getCurrentRouteParams();
    const { patientEncounterDeviceId } = event.value;

    applicationContext.redirect(ApplicationRoute.DEVICES_VIEWER, {
      encounterId,
      patientEncounterDeviceId,
      patientId
    }, { scrollToTop: true });
  }

  @OnClick({ target: SuepAliases.ACTION_VIEW_ALL })
  onViewAllButtonClick(): void {
    applicationContext.redirect(ApplicationRoute.SUEP_CLINICAL_NOTES_LIST);
  }

  @OnClick({ target: SuepAliases.ACTION_VIEW_ALL_NON_LAB_EXAMS })
  onViewAllNonLabButtonClick(): void {
    applicationContext.redirect(ApplicationRoute.SUEP_NON_LAB_EXAMS_LIST);
  }

  @OnClick({ target: SuepAliases.ACTION_VIEW_ALL_DIAGNOSIS })
  onViewAllDiagnosisButtonClick(): void {
    applicationContext.redirect(ApplicationRoute.SUEP_DIAGNOSIS_LIST);
  }

  @OnClick({ target: SuepAliases.ACTION_VIEW_ALL_LAB_EXAMS })
  onViewAllLabExamsButtonClick(): void {
    applicationContext.redirect(ApplicationRoute.SUEP_LAB_EXAMS_LIST);
  }

  @OnClick({ target: SuepAliases.ACTION_VIEW_ALL_VITAL_SIGN })
  onViewAllVitalSignButtonClick(): void {
    applicationContext.redirect(ApplicationRoute.SUEP_VITAL_SIGN_LIST);
  }

  @OnClick({ target: SuepAliases.ACTION_VIEW_ALL_INTAKES_OUTPUTS })
  onViewAllIntakesOutputsButtonClick(): void {
    applicationContext.redirect(ApplicationRoute.SUEP_INTAKES_OUTPUTS, { screen: Screen.INTAKES_AND_OUTPUT });
  }

  @OnClick({ target: SuepAliases.ACTION_VIEW_ALL_HYDRIC_BALANCE })
  onViewAllHydricBalanceButtonClick(): void {
    applicationContext.redirect(ApplicationRoute.SUEP_INTAKES_OUTPUTS, { screen: Screen.HYDRIC_BALANCE });
  }

  @OnClick(SuepAliases.ACTION_VIEW_ALL_DEVICES)
  onViewAllDevices(): void {
    applicationContext.redirect(ApplicationRoute.SUEP_DEVICES_LIST);
  }

  @OnClick(SuepAliases.ACTION_VIEW_ALL_CPOE)
  onViewAllCpoe(): void {
    applicationContext.redirect(ApplicationRoute.MODULE_CPOE);
  }

  @OnClick(SuepAliases.ACTION_VIEW_ALL_SCORE_INDEXES)
  onViewAllScoreIndexes(): void {
    applicationContext.redirect(ApplicationRoute.SUEP_SCORES_LIST);
  }

  @OnClose({ target: SuepAliases.MODAL_SUEP })
  onCloseModal(): void {
    this.suepPanelModalApi.setVisible(false);
  }
}

export default SuepController;
